import { z } from "zod";

export const classificationSchema = z.enum([
  "OPINION",
  "REAL_FACT",
  "PLAUSIBLE_LARP",
  "WEAK_OR_WRONG",
  "LOCAL_PASS",
  "WEAK_OR_NEEDS_MORE",
  "UNKNOWN",
  "WAITING",
]);

export const searchStatusSchema = z.enum([
  "SUPPORTED",
  "NOT_FOUND",
  "CONTRADICTED",
  "OPINION",
  "LOCAL_KNOWN_FACT",
  "NO_WEB_SEARCH",
  "WAITING",
  "UNKNOWN",
]);

export const sourceSchema = z.object({
  title: z.string(),
  url: z.string().url().or(z.literal("")),
});

export const memberSchema = z.object({
  name: z.string(),
  title: z.string(),
  bio: z.string(),
  style: z.string(),
  reward: z.string(),
});

export const answerAnalysisSchema = z.object({
  anchor: z.string(),
  actsLikeFact: z.boolean(),
  hasFeeling: z.boolean(),
});

export const fanChatRequestSchema = z.object({
  artist: z.string().min(1),
  username: z.string().min(1),
  stage: z.number().int().min(0).max(4),
  suspicion: z.number().int().min(0).max(10),
  currentPrompt: z.string().min(1),
  currentQuestionType: z.enum(["opinion", "receipt"]),
  currentAnswer: z.string(),
  rewards: z.array(z.string()),
  member: memberSchema,
  playerAnswer: z.string().min(1).max(3000),
  analysis: answerAnalysisSchema,
  actualFact: z.string().nullable(),
  larpNeedsFollowUp: z.boolean(),
  localScore: z.number(),
  requiredScore: z.number(),
  confidentLarp: z.boolean(),
});

export const aiJudgmentSchema = z.object({
  passed: z.boolean(),
  classification: z.enum(["OPINION", "REAL_FACT", "PLAUSIBLE_LARP", "WEAK_OR_WRONG"]),
  searchStatus: z.enum(["SUPPORTED", "NOT_FOUND", "CONTRADICTED", "OPINION"]),
  confidence: z.number().min(0).max(100),
  evidence: z.array(z.string()).max(3),
  reply: z.string().min(1).max(520),
});

export const fanChatResponseSchema = aiJudgmentSchema.extend({
  sources: z.array(sourceSchema).max(5),
  searched: z.boolean(),
  provider: z.enum(["ai_sdk", "responses_api", "local_fallback"]),
});

export const bossQuestionsRequestSchema = z.object({
  artist: z.string().min(1),
});

export const bossQuestionsResponseSchema = z.object({
  questions: z.array(z.string().min(1).max(260)).length(3),
  evidence: z.array(z.string()).max(3),
  sources: z.array(sourceSchema).max(5),
  searched: z.boolean(),
});

export const artistQuestionSchema = z.object({
  text: z.string().min(1).max(280),
  type: z.enum(["opinion", "receipt"]),
});

export const artistQuestionsRequestSchema = z.object({
  artist: z.string().min(1),
});

export const artistQuestionsResponseSchema = z.object({
  questions: z.array(artistQuestionSchema).length(5),
  evidence: z.array(z.string()).max(3),
  sources: z.array(sourceSchema).max(5),
  searched: z.boolean(),
});

export const aiDebugSchema = z.object({
  mode: z.string(),
  classification: classificationSchema,
  searchStatus: searchStatusSchema,
  confidence: z.number(),
  evidence: z.array(z.string()),
  sources: z.array(sourceSchema),
});

export type Classification = z.infer<typeof classificationSchema>;
export type SearchStatus = z.infer<typeof searchStatusSchema>;
export type Source = z.infer<typeof sourceSchema>;
export type Member = z.infer<typeof memberSchema>;
export type AnswerAnalysis = z.infer<typeof answerAnalysisSchema>;
export type FanChatRequest = z.infer<typeof fanChatRequestSchema>;
export type AiJudgment = z.infer<typeof aiJudgmentSchema>;
export type FanChatResponse = z.infer<typeof fanChatResponseSchema>;
export type BossQuestionsRequest = z.infer<typeof bossQuestionsRequestSchema>;
export type BossQuestionsResponse = z.infer<typeof bossQuestionsResponseSchema>;
export type ArtistQuestion = z.infer<typeof artistQuestionSchema>;
export type ArtistQuestionsRequest = z.infer<typeof artistQuestionsRequestSchema>;
export type ArtistQuestionsResponse = z.infer<typeof artistQuestionsResponseSchema>;
export type AiDebug = z.infer<typeof aiDebugSchema>;

export function parseFanChatRequest(input: unknown): FanChatRequest {
  return fanChatRequestSchema.parse(input);
}

export function parseFanChatResponse(input: unknown): FanChatResponse {
  return fanChatResponseSchema.parse(input);
}

export function parseBossQuestionsRequest(input: unknown): BossQuestionsRequest {
  return bossQuestionsRequestSchema.parse(input);
}

export function parseBossQuestionsResponse(input: unknown): BossQuestionsResponse {
  return bossQuestionsResponseSchema.parse(input);
}

export function parseArtistQuestionsRequest(input: unknown): ArtistQuestionsRequest {
  return artistQuestionsRequestSchema.parse(input);
}

export function parseArtistQuestionsResponse(input: unknown): ArtistQuestionsResponse {
  return artistQuestionsResponseSchema.parse(input);
}

export function parseAiJudgment(input: unknown): AiJudgment | null {
  const result = aiJudgmentSchema.safeParse(input);
  return result.success ? result.data : null;
}
